import type { ThemeKey } from "src/store/themeSlice";

/** How long the "New" badge stays visible after a theme's release date. */
export const NEW_THEME_HIGHLIGHT_DAYS = 10;

export type UiThemeOption = {
  key: ThemeKey;
  label: string;
  description: string;
  colors: string;
  preview: string;
  /** ISO date (YYYY-MM-DD) when the theme shipped — drives the New badge. */
  addedAt?: string;
};

export const UI_THEME_OPTIONS: readonly UiThemeOption[] = [
  {
    key: "indigo",
    label: "Indigo",
    description: "Deep indigo with pink accents",
    colors: "from-indigo-500 to-pink-500",
    preview: "bg-gradient-to-br from-indigo-600 via-indigo-800 to-pink-600",
  },
  {
    key: "emerald",
    label: "Emerald",
    description: "Fresh greens and cyan highlights",
    colors: "from-emerald-500 to-cyan-500",
    preview: "bg-gradient-to-br from-emerald-600 via-emerald-800 to-cyan-600",
  },
  {
    key: "violet",
    label: "Violet",
    description: "Rich violet and fuchsia glow",
    colors: "from-violet-500 to-fuchsia-500",
    preview: "bg-gradient-to-br from-violet-600 via-violet-800 to-fuchsia-600",
  },
  {
    key: "rose",
    label: "Rose",
    description: "Warm rose with orange warmth",
    colors: "from-rose-500 to-orange-500",
    preview: "bg-gradient-to-br from-rose-600 via-rose-800 to-orange-600",
  },
  {
    key: "slate",
    label: "Slate",
    description: "Cool slate with blue and purple",
    colors: "from-blue-500 to-purple-500",
    preview: "bg-gradient-to-br from-slate-600 via-slate-800 to-purple-600",
  },
  {
    key: "liftoff",
    label: "Liftoff",
    description: "Deep space indigo with launch-pad glow and stars",
    colors: "from-indigo-400 to-indigo-700",
    preview:
      "bg-[radial-gradient(ellipse_at_50%_100%,rgba(99,102,241,0.35),transparent_55%),linear-gradient(to_bottom_right,#312e81,#111827,#000)]",
    addedAt: "2026-07-04",
  },
  {
    key: "midnight",
    label: "Midnight",
    description: "Near-black dark mode with moonlight glow and stars",
    colors: "from-zinc-500 to-zinc-800",
    preview:
      "bg-[radial-gradient(ellipse_at_50%_0%,rgba(148,163,184,0.2),transparent_50%),linear-gradient(to_bottom_right,#09090b,#030712,#000)]",
    addedAt: "2026-07-04",
  },
] as const;

export function isThemeNew(
  theme: UiThemeOption,
  nowMs: number = Date.now(),
): boolean {
  if (!theme.addedAt) return false;
  const addedMs = new Date(`${theme.addedAt}T00:00:00`).getTime();
  if (Number.isNaN(addedMs)) return false;
  const highlightMs = NEW_THEME_HIGHLIGHT_DAYS * 24 * 60 * 60 * 1000;
  return nowMs < addedMs + highlightMs;
}

/** New themes first (newest release first), then the rest in catalog order. */
export function getThemeOptionsForSettings(
  nowMs: number = Date.now(),
): UiThemeOption[] {
  const indexed = UI_THEME_OPTIONS.map((theme, index) => ({ theme, index }));

  return indexed
    .sort((a, b) => {
      const aNew = isThemeNew(a.theme, nowMs);
      const bNew = isThemeNew(b.theme, nowMs);
      if (aNew !== bNew) return aNew ? -1 : 1;
      if (aNew && bNew && a.theme.addedAt && b.theme.addedAt) {
        return b.theme.addedAt.localeCompare(a.theme.addedAt);
      }
      return a.index - b.index;
    })
    .map(({ theme }) => theme);
}

/**
 * Campaign id for the "new themes" announcement (see the announcement
 * registry). Bump when shipping a new batch of themes to re-show the prompt.
 */
export const THEME_ANNOUNCEMENT_ID = "2026-07-liftoff-midnight";

export function getCurrentlyNewThemes(
  nowMs: number = Date.now(),
): UiThemeOption[] {
  return UI_THEME_OPTIONS.filter((theme) => isThemeNew(theme, nowMs));
}

export function formatNewThemeNames(themes: UiThemeOption[]): string {
  const labels = themes.map((t) => t.label);
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}
